//admin/livreurs/PrimeOverview.vue
<template>
  <div class="prime-overview">
    <!-- En-tête fixe avec sélecteur de période -->
    <div class="sticky top-0 z-1000 bg-white shadow-md">
      <div class="period-selector p-4 mb-4">
        <Dropdown v-model="selectedPeriod" :options="periodOptions" optionLabel="label"
          placeholder="Sélectionner une période" class="w-full md:w-96" @change="onPeriodChange" />
      </div>

      <div class="px-4 pb-4">
        <div class="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <Card class="w-full md:w-auto shadow-sm">
            <template #title>
              <div class="flex items-center gap-2">
                <i class="pi pi-euro text-xl"></i>
                <span class="text-lg">Prime sur la periode</span>
              </div>
            </template>
            <template #content>
              <span class="text-2xl font-bold text-blue-600">
                {{ primeData?.total_prime?.toFixed(2) || '0.00' }}€
              </span>
            </template>
          </Card>
        </div>
      </div>
    </div>

    <!-- États de chargement et d'erreur -->
    <div v-if="loading" class="flex-1 flex items-center justify-center p-8">
      <ProgressSpinner />
    </div>

    <div v-else-if="error" class="flex-1 flex flex-col items-center justify-center p-4">
      <Message severity="error" :closable="false" class="mb-4 w-full max-w-lg">{{ error }}</Message>
      <Button label="Réessayer" icon="pi pi-refresh" @click="fetchDatas" />
    </div>

    <!-- Liste des jours -->
    <div v-else class="flex-1 p-4 z-10">
      <TransitionGroup name="list" tag="div" class="space-y-4">
        <div v-for="(detail, date) in sortedDetails" :key="date"
          class="day-card bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow">
          <div class="cursor-pointer p-4 relative" @click="toggleDate(date)">
            <div class="flex justify-between items-center">
              <div class="flex-1">
                <h3 class="text-lg font-semibold">{{ formatDate(date) }}</h3>
                <Badge :value="detail.prime.toFixed(2) + '€'" :severity="detail.prime > 0 ? 'success' : 'danger'"
                  class="mt-1" />
              </div>
              <Button :icon="expandedDate === date ? 'pi pi-chevron-up' : 'pi pi-chevron-down'" 
                     text rounded 
                     class="toggle-button z-0" />
            </div>
          </div>

          <div v-show="expandedDate === date"
            class="px-4 pb-4 space-y-4 transition-all duration-300 ease-in-out"
            :class="{ 'max-h-[2000px] opacity-100': expandedDate === date, 'max-h-0 opacity-0': expandedDate !== date }">
            
            <!-- Message de succès -->
            <div v-if="detail.prime > 0" class="bg-green-50 p-4 rounded-lg">
              <div class="text-green-800">
                <h3 class="font-bold mb-2">🌟 Félicitations !</h3>
                <p class="mb-3">Excellent travail aujourd'hui ! Vous avez maintenu un haut niveau de qualité dans vos
                  livraisons.</p>

                <ul class="space-y-2">
                  <li class="flex items-start">
                    <span class="mr-2">✓</span>
                    <span>Plus de 90% de retours cageots</span>
                  </li>
                  <li class="flex items-start">
                    <span class="mr-2">✓</span>
                    <span>Tournée réalisée complètement</span>
                  </li>
                  <li class="flex items-start">
                    <span class="mr-2">✓</span>
                    <span>Aucune réclamation des clients de votre tournée</span>
                  </li>
                </ul>
              </div>
            </div>

            <!-- Ratios -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div class="bg-white p-4 rounded-lg shadow">
                <div class="text-gray-600 mb-2">Cageots contrôlés par responsable</div>
                <div :class="detail.ratio_retour_cageot_controleur < 90 ? 'text-red-600' : 'text-green-600'"
                  class="text-2xl font-bold">
                  {{ detail.cageots_comptes_controleur }} cageots ({{ detail.ratio_retour_cageot_controleur }}%)
                </div>
              </div>
              <div class="bg-white p-4 rounded-lg shadow">
                <div class="text-gray-600 mb-2">Cageots que vous avez saisi sur votre téléphone</div>
                <div class="text-2xl font-bold text-blue-600">
                  {{ detail.cageots_saisis_livreurs }} cageots ({{ detail.ratio_retour_cageot_livreur }}%)
                </div>
              </div>
            </div>

            <!-- Raisons prime nulle -->
            <div v-if="detail.raisons_prime_zero && detail.raisons_prime_zero.length">
              <h4 class="text-lg font-semibold mb-3">Raisons prime nulle</h4>
              <div class="space-y-3">
                <Panel v-for="(raison, index) in detail.raisons_prime_zero" :key="index" :header="raison.message"
                  class="shadow-none border-red-200 bg-red-50">
                  <template #icons>
                    <i class="pi pi-exclamation-triangle text-red-500"></i>
                  </template>
                  <div v-if="raison.tips" class="mt-3 p-3 bg-green-50 rounded-lg">
                    <div class="flex items-start gap-2">
                      <i class="pi pi-info-circle text-green-600 mt-1"></i>
                      <div>
                        <h5 class="font-semibold text-green-700 mb-1">Comment améliorer ?</h5>
                        <p class="text-green-600">{{ raison.tips }}</p>
                      </div>
                    </div>
                  </div>
                </Panel>
              </div>
            </div>

            <!-- Réclamations -->
            <div v-if="detail.reclamations && detail.reclamations.length">
              <h4 class="text-lg font-semibold mb-3">Réclamations</h4>
              <div class="space-y-3">
                <Card v-for="reclamation in detail.reclamations" :key="reclamation.id" class="shadow-none">
                  <template #content>
                    <p class="mb-3">{{ reclamation.commentaire }}</p>
                    <div v-if="reclamation.files?.length"
                      class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 mb-3">
                      <div v-for="file in reclamation.files" :key="file.id" class="relative group cursor-pointer"
                        @click="openImageModal(file.url)">
                        <img :src="file.url" :alt="'Photo réclamation ' + reclamation.id"
                          class="w-full h-24 object-cover rounded-lg" />
                        <div
                          class="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-opacity rounded-lg">
                        </div>
                      </div>
                    </div>
                    <div class="text-sm text-gray-600">
                      <p class="font-medium">{{ reclamation.customer.name }}</p>
                      <p>{{ reclamation.customer.address }}</p>
                    </div>
                  </template>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </TransitionGroup>
    </div>

    <!-- Modal d'image -->
    <Dialog v-model:visible="isImageModalVisible" modal dismissableMask :style="{ width: '90vw' }"
      :breakpoints="{ '960px': '75vw', '641px': '90vw' }">
      <img :src="selectedImage" alt="Image en grand format" class="w-full h-auto max-h-[80vh] object-contain" />
    </Dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import axios from '@/api/axios';
import Dropdown from 'primevue/dropdown';

const primeData = ref(null);
const loading = ref(true);
const error = ref(null);
const expandedDate = ref(null);
const selectedImage = ref(null);
const selectedPeriod = ref(null);
const isImageModalVisible = computed(() => !!selectedImage.value);

const generatePeriodOptions = () => {
  const options = [];
  const today = new Date();

  // Fonction pour formater la date sans décalage UTC
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Option "Cette semaine"
  const weekStart = new Date(today);
  weekStart.setDate(today.getDate() - today.getDay() + 1);
  const weekEnd = new Date(today);
  weekEnd.setDate(today.getDate() + (7 - today.getDay()));

  options.push({
    label: 'Cette semaine',
    startDate: formatDate(weekStart),
    endDate: formatDate(weekEnd)
  });

  // 6 derniers mois
  for (let i = 0; i < 6; i++) {
    const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
    const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);

    options.push({
      label: date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }),
      startDate: formatDate(date),
      endDate: formatDate(lastDay)
    });
  }

  return options;
};

const periodOptions = ref(generatePeriodOptions());

// Modification de la fonction fetchDatas pour inclure les dates
const fetchDatas = async (startDate = null, endDate = null) => {
  loading.value = true;
  error.value = null;
  try {
    let url = 'stats/primes/';
    if (startDate && endDate) {
      url += `?start_date=${startDate}&end_date=${endDate}`;
    }
    const response = await axios.get(url);
    primeData.value = response.data;
  } catch (err) {
    error.value = 'Erreur lors de la récupération des données. Veuillez réessayer.';
    console.error('Erreur lors de la récupération des données des primes:', err);
  } finally {
    loading.value = false;
  }
};

// Fonction pour gérer le changement de période
const onPeriodChange = () => {
  if (selectedPeriod.value) {
    fetchDatas(selectedPeriod.value.startDate, selectedPeriod.value.endDate);
  }
};

const sortedDetails = computed(() => {
  if (!primeData.value?.details) return {};
  return Object.fromEntries(
    Object.entries(primeData.value.details)
      .sort(([dateA], [dateB]) => new Date(dateB) - new Date(dateA))
  );
});

const toggleDate = (date) => {
  expandedDate.value = expandedDate.value === date ? null : date;
};

const formatDate = (dateStr) => {
  return new Intl.DateTimeFormat('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  }).format(new Date(dateStr));
};

const openImageModal = (imageUrl) => {
  selectedImage.value = imageUrl;
};

onMounted(() => {
  selectedPeriod.value = periodOptions.value[0];
  fetchDatas(selectedPeriod.value.startDate, selectedPeriod.value.endDate);
});
</script>

<style scoped>
.prime-overview {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #f8fafc;
}

.list-move,
.list-enter-active,
.list-leave-active {
  transition: all 0.5s ease;
}

.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: translateX(-30px);
}

.list-leave-active {
  position: absolute;
}

/* Ajustement des z-index pour éviter les conflits */
.toggle-button {
  position: relative;
  z-index: 0 !important;
}

/* Override PrimeVue styles */
:deep(.p-dropdown) {
  width: 100%;
}

:deep(.p-card) {
  border-radius: 0.75rem;
}

:deep(.p-panel .p-panel-header) {
  padding: 1rem;
  background: transparent;
}

:deep(.p-message) {
  border-radius: 0.75rem;
}

:deep(.p-dialog) {
  border-radius: 1rem;
}

:deep(.p-badge) {
  font-weight: 600;
}

/* Fix pour les boutons dans les day-card */
.day-card {
  position: relative;
}

/* Prévenir les boutons de chevrons de passer au-dessus des autres éléments */
:deep(.p-button.p-button-text) {
  z-index: 0 !important;
}

/* Amélioration pour le mobile */
@media (max-width: 640px) {
  .period-selector {
    padding: 0.75rem;
  }
  
  .day-card {
    border-radius: 0.5rem;
  }
}
</style>